import { useT } from "../../i18n/index.js";
import { deltaColorClass, formatMoney, formatPctSigned, toNumber } from "../../lib/format-fr.js";

export type Row = {
  label: string;
  realized: number | string | null | undefined;
  annualized?: number | string | null | undefined;
  budget?: number | string | null | undefined;
  /** Forçar destaque (linha de subtotal/total) */
  emphasis?: "subtotal" | "total" | null;
  /** Indenta a label (linha-filha) */
  indent?: boolean;
  /** Hint em hover (fórmula) */
  hint?: string;
};

type ColumnConfig = {
  showAnnualized?: boolean;
  showBudget?: boolean;
  showVariance?: boolean;
};

function variance(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
): { abs: number | null; pct: number | null } {
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na === null || nb === null) return { abs: null, pct: null };
  const abs = na - nb;
  const pct = nb !== 0 ? (abs / Math.abs(nb)) * 100 : null;
  return { abs, pct };
}

export function ComparisonTable({
  rows,
  columns,
}: {
  rows: ReadonlyArray<Row>;
  columns?: ColumnConfig;
}) {
  const t = useT();
  const showAnnualized = columns?.showAnnualized ?? true;
  const showBudget = columns?.showBudget ?? true;
  const showVariance = columns?.showVariance ?? true;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
            <th className="text-left px-3 py-2">{t("comparisonTable.colRubrique")}</th>
            <th className="text-right px-3 py-2 font-mono">{t("comparisonTable.colExercice")}</th>
            {showAnnualized && (
              <th className="text-right px-3 py-2 font-mono">
                {t("comparisonTable.colAnnualized")}
              </th>
            )}
            {showBudget && (
              <th className="text-right px-3 py-2 font-mono">{t("comparisonTable.colBudget")}</th>
            )}
            {showVariance && showBudget && (
              <th className="text-right px-3 py-2 font-mono">
                {t("comparisonTable.colVarianceEur")}
              </th>
            )}
            {showVariance && showBudget && (
              <th className="text-right px-3 py-2 font-mono">
                {t("comparisonTable.colVariancePct")}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-900">
          {rows.map((r, i) => {
            const v =
              showVariance && showBudget
                ? variance(r.realized, r.budget)
                : { abs: null, pct: null };
            const emphCls =
              r.emphasis === "total"
                ? "bg-lime/5 font-semibold"
                : r.emphasis === "subtotal"
                  ? "bg-neutral-800/30 font-medium"
                  : "";
            const indentCls = r.indent ? "pl-8" : "px-3";
            return (
              <tr key={i} className={emphCls} title={r.hint}>
                <td className={`${indentCls} py-1.5 text-neutral-200`}>{r.label}</td>
                <td className="text-right px-3 py-1.5 font-mono tabular-nums">
                  {formatMoney(r.realized)}
                </td>
                {showAnnualized && (
                  <td className="text-right px-3 py-1.5 font-mono tabular-nums text-neutral-400">
                    {r.annualized === undefined ? "—" : formatMoney(r.annualized)}
                  </td>
                )}
                {showBudget && (
                  <td className="text-right px-3 py-1.5 font-mono tabular-nums text-neutral-400">
                    {r.budget === undefined ? "—" : formatMoney(r.budget)}
                  </td>
                )}
                {showVariance && showBudget && (
                  <td
                    className={`text-right px-3 py-1.5 font-mono tabular-nums ${deltaColorClass(v.abs)}`}
                  >
                    {v.abs === null ? "—" : formatMoney(v.abs)}
                  </td>
                )}
                {showVariance && showBudget && (
                  <td
                    className={`text-right px-3 py-1.5 font-mono tabular-nums ${deltaColorClass(v.pct)}`}
                  >
                    {v.pct === null ? "—" : formatPctSigned(v.pct, { alreadyPct: true })}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
